import {
  doApiWriteRequest,
  doApiReadRequest,
  doGraphqlRequest,
} from 'ducks/requests'
import {
  collectionQuery,
  generateUpdateMutation,
  allFields,
  DESTROY_MUTATION,
} from 'ducks/mailboxes/api'
import { selectCurrentChannels } from 'ducks/channels/selectors'
import { createDoFetchInMemoryByQueryId } from 'ducks/searches/operations/createDoFetchInMemoryByQueryId'
import { convertHexToRGBA } from 'util/colors'
import { getRawId } from 'util/globalId'
import { FETCH_CHANNELS } from 'ducks/channels/actionTypes'
import { changeEntity } from 'ducks/entities/actionUtils'
import { doBuildInboxMenuFromMailboxes } from 'ducks/folders/operations/collections'
import {
  GMAIL_IMPORT,
  SEND_VERIFICATION,
  SEND_SETUP_INSTRUCTIONS,
  FETCH_MAILBOXES,
  UPDATE_MAILBOX,
  UPDATE_MAILBOX_SUCCESS,
  TEST_SMTP,
  CHECK_DNS,
  DESTROY_MAILBOX,
} from './actionTypes'
import {
  mailboxGraphQlResponseSchema,
  mailboxUpdateGraphQlResponseSchema,
  mailboxDeleteGraphQlResponseSchema,
} from './schema'

const LOAD_ALL_MAILBOXES_QUERYID = 'entityType:channel pageSize:10000'

export const doGmailImport = (
  id,
  {
    import_history: importHistory = false,
    import_labels: importLabels = false,
    close_tickets: importCloseTickets = false,
    pages_to_import: pagesToImport = 1,
  } = {},
  options = {}
) => async dispatch => {
  if (!importHistory) return null
  const payload = {
    mailbox_id: id,
    process_emails: importHistory,
    close_tickets: importCloseTickets,
    process_labels: importLabels,
    pages_to_import: pagesToImport,
  }

  return dispatch(
    doApiWriteRequest(GMAIL_IMPORT, 'api/gmail_imports', payload, {
      ...options,
      throwOnError: true,
      method: 'POST',
    })
  )
}

export const doSendVerification = (id, options) => dispatch => {
  return dispatch(
    doApiWriteRequest(
      SEND_VERIFICATION,
      `v1/mailboxes/${id}/send_forwarding_verification`,
      {},
      {
        ...options,
        throwOnError: true,
        method: 'POST',
      }
    )
  )
}

export const doSendSetupInstructions = (id, payload, options) => dispatch => {
  return dispatch(
    doApiWriteRequest(
      SEND_SETUP_INSTRUCTIONS,
      `v1/mailboxes/${id}/send_forwarding_instructions`,
      payload,
      {
        ...options,
        throwOnError: true,
        method: 'POST',
      }
    )
  )
}

function doFetchMailboxes(
  options = {
    requestType: FETCH_MAILBOXES,
    variables: { include_inaccessible: false },
  }
) {
  const { requestType, variables } = options

  const queryId = LOAD_ALL_MAILBOXES_QUERYID
  const cursor = 'all'
  return doGraphqlRequest(requestType, collectionQuery(), variables, {
    normalizationSchema: mailboxGraphQlResponseSchema,
    searches: {
      queryId,
      cursor,
      extractPagination: ({ mailboxes: nodes }) => {
        return {
          type: 'channel',
          nodes,
          startCursor: 1,
          endCursor: 1,
          hasNextPage: false,
          hasPreviousPage: false,
          totalCount: nodes.length,
          totalPageCount: 1,
        }
      },
    },
    throwOnError: true,
    includeLegacyPayload: true,
  })
}

export const doFetchMailboxesIncludingInaccessible = () => dispatch => {
  return dispatch(
    doFetchMailboxes(
      // deliberately a different request type so it does not update state.mailboxes
      // otherwise it would include the inaccessible mailboxes there
      { requestType: FETCH_CHANNELS, variables: { include_inaccessible: true } }
    )
  )
}

export const doFetchMailboxesInMemory = createDoFetchInMemoryByQueryId({
  fromQueryId: LOAD_ALL_MAILBOXES_QUERYID,
  entityType: 'channel',
  doLoadAllFn: doFetchMailboxesIncludingInaccessible,
})

function calculateMailboxPosition(mailbox, currentOrderedMailboxes) {
  if (currentOrderedMailboxes[mailbox.position + 1]) {
    return currentOrderedMailboxes[mailbox.position + 1].position - 10
  } else if (currentOrderedMailboxes[mailbox.position - 1]) {
    return currentOrderedMailboxes[mailbox.position - 1].position + 10
  }
  return mailbox.position
}

export const doUpdateMailbox = (id, mailbox, options = {}) => async (
  dispatch,
  getState
) => {
  const state = getState()
  const {
    responseFields = allFields,
    updatePosition = false,
    queryId,
    orderedIds = null,
  } = options
  const orderedChannels = selectCurrentChannels(state)

  const mailboxInput = { ...mailbox }
  delete mailboxInput.forward_email_address
  delete mailboxInput.id
  delete mailboxInput.position
  const optimisticMailbox = {
    ...mailbox,
  }
  if (mailboxInput.color && mailboxInput.color.startsWith('#')) {
    mailboxInput.color = convertHexToRGBA(mailboxInput.color)
  }
  if (updatePosition) {
    mailboxInput.row_order_position = mailbox.position
    optimisticMailbox.position = calculateMailboxPosition(
      mailbox,
      orderedChannels
    )
  }

  const entityId = id
  const entityType = 'channel'

  const optimist = {
    entities: {
      [entityType]: {
        [entityId]: optimisticMailbox,
      },
    },
  }

  const response = await dispatch(
    doGraphqlRequest(
      UPDATE_MAILBOX,
      generateUpdateMutation(responseFields),
      {
        id,
        input: mailboxInput,
      },
      {
        normalizationSchema: mailboxUpdateGraphQlResponseSchema,
        throwOnError: true,
        includeLegacyPayload: true,
        optimist,
        searches: {
          additionalActions: [
            {
              type: 'INVALIDATE_ENTITIES',
              entityTypes: [entityType],
              phases: ['SUCCESS'],
            },
            {
              type: 'UPDATE_CURSOR',
              queryId,
              entityIds: orderedIds,
              phases: ['STARTED'],
            },
          ],
        },

        // Required to make the format of UPDATE_MAILBOX_SUCCESS backward compatibile
        // with existing reducers
        transformResponse: data => {
          return {
            mailbox: data.updateMailbox,
          }
        },
        ...options,
      }
    )
  )

  dispatch(doBuildInboxMenuFromMailboxes())

  return response
}

export function doUpdateMailboxLocally(mailbox) {
  if (!mailbox) return false
  return {
    type: UPDATE_MAILBOX_SUCCESS,
    payload: {
      mailbox,
    },
    ...changeEntity('channel', mailbox.id, mailbox),
  }
}

export const doTestMailboxSmtp = (
  { id, username, password, authentication, address, port, useSsl },
  options = {}
) => dispatch => {
  return dispatch(
    doApiWriteRequest(
      TEST_SMTP,
      `api/settings/mailboxes/test_smtp`,
      {
        smtp: {
          id,
          user_name: username,
          password,
          authentication,
          address,
          port,
          use_ssl: useSsl ? '1' : '0',
        },
      },
      {
        ...options,
        throwOnError: true,
        method: 'POST',
      }
    )
  )
}

export const doCheckMailboxDns = (
  mailboxId,
  { email, validate },
  options = {}
) => dispatch => {
  return dispatch(
    doApiReadRequest(
      CHECK_DNS,
      `api/settings/mailboxes/${mailboxId}/dns_check`,
      { email, validate },
      {
        ...options,
        throwOnError: true,
        method: 'GET',
      }
    )
  )
}

export const doDeleteMailbox = (gid, options = {}) => {
  return doGraphqlRequest(
    DESTROY_MAILBOX,
    DESTROY_MUTATION,
    {
      id: gid,
    },
    {
      throwOnError: true,
      normalizationSchema: mailboxDeleteGraphQlResponseSchema,
      includeLegacyPayload: true,
      transformResponse: () => {
        return {
          // HACK (jscheel): API doesn't return anything and legacy handlers
          // expect a raw scatterswapped id
          id: getRawId(gid),
          gid,
        }
      },
      searches: {
        additionalActions: [
          {
            type: 'INVALIDATE_ENTITIES',
            entityTypes: ['channel'],
            phases: ['SUCCESS'],
          },
        ],
      },
      ...options,
      moduleOptions: {
        ...options.moduleOptions,
        entities: {
          targetOperation: 'remove',
          additionalActions: [
            {
              entityType: 'channel',
              entityId: gid,
              stores: ['pending', 'current'],
              operation: 'remove',
              phases: ['SUCCESS'],
            },
          ],
        },
      },
    }
  )
}
